// Rename (PATCH /v1/agents/{id}) + archive (DELETE /v1/agents/{id}) mutations
// for the Agent route container (agent.md; hoisted out of Agent.tsx to hold
// its route-orchestration line budget — a seam, not a decision change).
// Cache discipline: rename writes the detail key and both invalidate
// `agents.all`; the caller owns popover state and post-archive navigation.
import { useMutation, useQueryClient } from "@tanstack/react-query";

import type { components } from "../../api/schema";
import { apiClient } from "../../lib/apiClient";
import { ApiRequestError, requestErrorDescription } from "../../lib/errors";
import { queryKeys } from "../../lib/queryKeys";
import { useToast } from "../../ui";

type AgentPatch = components["schemas"]["AgentPatch"];

export interface AgentActionsOptions {
  agentId: string | undefined;
  // Runs after a successful archive is invalidated: clear scope + navigate.
  onArchived: () => void;
  // Close the archive confirm popover on success.
  onCloseArchiveConfirm: () => void;
  // Close the rename popover on success.
  onCloseRenameForm: () => void;
}

export function useAgentActions({
  agentId,
  onArchived,
  onCloseArchiveConfirm,
  onCloseRenameForm,
}: AgentActionsOptions) {
  const queryClient = useQueryClient();
  const toast = useToast();

  const renameAgent = useMutation({
    mutationFn: async (displayName: string) => {
      if (!agentId) {
        throw new Error("Agent id is required.");
      }
      const body = { display_name: displayName } satisfies AgentPatch;
      const { data, error } = await apiClient.PATCH("/v1/agents/{id}", {
        body,
        params: { path: { id: agentId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
    onSuccess: async (updatedAgent) => {
      onCloseRenameForm();
      queryClient.setQueryData(
        queryKeys.agents.detail(updatedAgent.id),
        updatedAgent,
      );
      toast.success("Agent renamed");
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
    },
    onError: (error) => {
      toast.error("Agent rename failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  const archiveAgent = useMutation({
    mutationFn: async () => {
      if (!agentId) {
        throw new Error("Agent id is required.");
      }
      const { error } = await apiClient.DELETE("/v1/agents/{id}", {
        params: { path: { id: agentId } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      onCloseArchiveConfirm();
      toast.success("Agent archived");
      await queryClient.invalidateQueries({ queryKey: queryKeys.agents.all });
      onArchived();
    },
    onError: (error) => {
      toast.error("Agent archive failed", {
        description: requestErrorDescription(error),
      });
    },
  });

  return { archiveAgent, renameAgent };
}
